import { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { ListItemText, Menu, MenuItem, makeStyles } from '@material-ui/core';

import { RiArrowRightSLine } from '@remixicon/react';
import capitalize from 'lodash/capitalize';

import { KeyboardShortcut } from '../KeyboardShortcut';
import { type FilterDefinition, useFilters } from './provider';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 200,
  },
  submenuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  arrowIcon: {
    marginLeft: theme.spacing(1),
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
}));

interface CategorySubmenuProps {
  category: string;
  filters: FilterDefinition[];
  activeFilters: { id: string }[];
  onSelect: (filter: FilterDefinition) => void;
  parentMenuOpen: boolean;
}

function CategorySubmenu({
  category,
  filters,
  activeFilters,
  onSelect,
  parentMenuOpen,
}: CategorySubmenuProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuItemRef = useRef<HTMLLIElement>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleSubmenuMouseLeave = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowRight' || event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSubmenuKeyDown = (event: React.KeyboardEvent) => {
    // Stop all keyboard events from bubbling to parent menu
    event.stopPropagation();

    if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault();
      setAnchorEl(null);
      menuItemRef.current?.focus();
    }
  };

  const allDisabled = filters.every(f =>
    activeFilters.some(af => af.id === f.id),
  );

  useEffect(() => {
    // Close submenu when parent menu closes
    if (!parentMenuOpen && anchorEl) {
      setAnchorEl(null);
    }
  }, [parentMenuOpen, anchorEl]);

  return (
    <>
      <MenuItem
        ref={menuItemRef}
        dense
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        className={classes.submenuItem}
        disabled={allDisabled}
      >
        <ListItemText primary={category} />
        <RiArrowRightSLine className={classes.arrowIcon} />
      </MenuItem>
      <Menu
        open={Boolean(anchorEl) && parentMenuOpen}
        anchorEl={anchorEl}
        autoFocus
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          onMouseLeave: handleSubmenuMouseLeave,
          onMouseEnter: () => setAnchorEl(anchorEl),
          onKeyDown: handleSubmenuKeyDown,
          variant: 'menu',
          className: classes.root,
        }}
        style={{ pointerEvents: 'none' }}
        PaperProps={{ style: { pointerEvents: 'auto' } }}
      >
        {filters.map(filter => (
          <MenuItem
            dense
            key={filter.id}
            onClick={() => onSelect(filter)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
            disabled={activeFilters.some(f => f.id === filter.id)}
          >
            <ListItemText primary={filter.label ?? capitalize(filter.id)} />
            {filter.hotkey && (
              <span>
                <KeyboardShortcut>{filter.hotkey}</KeyboardShortcut>
              </span>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Component that renders a dropdown of filters to choose from.
 */
export function FilterSelect({
  filters,
  anchorEl,
  onClose,
  onSelect,
}: {
  filters: FilterDefinition[];
  anchorEl: Element | null;
  onClose: () => void;
  onSelect: (filter: FilterDefinition) => void;
}) {
  const classes = useStyles();
  const { activeFilters } = useFilters();

  const hotkeys = useMemo(
    () => filters.filter(f => f.hotkey).map(f => f.hotkey!),
    [filters],
  );

  const ref = useHotkeys(hotkeys, (event, handler) => {
    const filter = filters.find(f => f.hotkey === handler.hotkey);
    if (filter) {
      event.preventDefault();
      onSelect(filter);
    }
  });

  const { uncategorizedFilters, categorizedFilters } = useMemo(() => {
    const uncategorized: FilterDefinition[] = [];
    const byCategory: Map<string, FilterDefinition[]> = new Map();

    for (const filter of filters) {
      if (filter.category) {
        const existing = byCategory.get(filter.category) ?? [];
        byCategory.set(filter.category, [...existing, filter]);
      } else {
        uncategorized.push(filter);
      }
    }

    return {
      uncategorizedFilters: uncategorized,
      categorizedFilters: byCategory,
    };
  }, [filters]);

  const isOpen = Boolean(anchorEl);

  return (
    <Menu
      open={isOpen}
      onClose={onClose}
      anchorEl={anchorEl}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      MenuListProps={{
        ref,
        className: classes.root,
      }}
    >
      {uncategorizedFilters.map(filter => (
        <MenuItem
          dense
          key={filter.id}
          onClick={() => onSelect(filter)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
          disabled={activeFilters.some(f => f.id === filter.id)}
        >
          <ListItemText primary={filter.label ?? capitalize(filter.id)} />
          {filter.hotkey && (
            <span>
              <KeyboardShortcut>{filter.hotkey}</KeyboardShortcut>
            </span>
          )}
        </MenuItem>
      ))}
      {Array.from(categorizedFilters.entries()).map(
        ([category, categoryFilters]) => (
          <CategorySubmenu
            key={category}
            category={category}
            filters={categoryFilters}
            activeFilters={activeFilters}
            onSelect={onSelect}
            parentMenuOpen={isOpen}
          />
        ),
      )}
    </Menu>
  );
}
